import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import { vouchersApi, propertiesApi, profilesApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';

interface Property {
  id: string;
  title: string;
  property_code?: string;
}

interface Tenant {
  id: string;
  first_name: string;
  last_name: string;
}

export default function VoucherEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['finance', 'common']);
  const user = useAppStore((state) => state.user);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [voucherType, setVoucherType] = useState<string>('receipt');
  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('SAR');
  const [description, setDescription] = useState<string>('');
  const [propertyId, setPropertyId] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');

  // Fetch voucher data
  const { data: voucher, loading: loadingVoucher, error: fetchError } = useApi(
    async () => {
      if (!id) throw new Error('No voucher ID provided');
      const response = await vouchersApi.getById(id);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    [id]
  );

  // Fetch properties
  const { data: properties } = useApi<Property[]>(
    async () => {
      const response = await propertiesApi.getAll();
      if (response.error) throw new Error(response.error.message);
      return response.data || [];
    },
    []
  );

  // Fetch tenants (profiles with role='tenant')
  const { data: tenants } = useApi<Tenant[]>(
    async () => {
      const response = await profilesApi.getTenants();
      if (response.error) throw new Error(response.error.message);
      return response.data || [];
    },
    []
  );

  // Populate form when voucher data loads
  useEffect(() => {
    if (voucher) {
      setVoucherType(voucher.voucher_type || 'receipt');
      setAmount(voucher.amount?.toString() || '');
      setCurrency(voucher.currency || 'SAR');
      setDescription(voucher.description || '');
      setPropertyId(voucher.property_id || '');
      setTenantId(voucher.tenant_id || '');
      setPaymentMethod(voucher.payment_method || '');
    }
  }, [voucher]);

  // Redirect if not manager/admin
  useEffect(() => {
    if (!isManagerOrAdmin) {
      navigate('/dashboard/finance/vouchers');
    }
  }, [isManagerOrAdmin, navigate]);

  // Redirect if voucher is not in draft status
  useEffect(() => {
    if (voucher && voucher.status !== 'draft') {
      navigate(`/dashboard/finance/vouchers/${id}`);
    }
  }, [voucher, id, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData = {
        voucher_type: voucherType,
        amount: parseFloat(amount),
        currency,
        description,
        property_id: propertyId || null,
        tenant_id: tenantId || null,
        payment_method: paymentMethod || null,
      };

      const response = await vouchersApi.update(id, updateData);

      if (response.error) {
        throw new Error(response.error.message);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/dashboard/finance/vouchers/${id}`);
      }, 1500);
    } catch (err: any) {
      setError(err.message || t('common:somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  if (loadingVoucher) {
    return (
      <ResponsiveContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </ResponsiveContainer>
    );
  }

  if (fetchError || !voucher) {
    return (
      <ResponsiveContainer>
        <Alert severity="error" sx={{ mb: 3 }}>
          {fetchError || t('common:somethingWentWrong')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/finance/vouchers')}
        >
          {t('common:back')}
        </Button>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/dashboard/finance/vouchers/${id}`)}
          variant="outlined"
        >
          {t('common:back')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {t('editVoucher')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('voucherUpdated')}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Voucher Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('voucherType')}</InputLabel>
                  <Select
                    value={voucherType}
                    label={t('voucherType')}
                    onChange={(e) => setVoucherType(e.target.value)}
                    required
                  >
                    <MenuItem value="receipt">{t('voucherTypes.receipt')}</MenuItem>
                    <MenuItem value="payment">{t('voucherTypes.payment')}</MenuItem>
                    <MenuItem value="journal">{t('voucherTypes.journal')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Amount */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('amount')}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              {/* Currency */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('currency')}</InputLabel>
                  <Select
                    value={currency}
                    label={t('currency')}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    <MenuItem value="SAR">SAR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('paymentMethod')}</InputLabel>
                  <Select
                    value={paymentMethod}
                    label={t('paymentMethod')}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <MenuItem value="">{t('common:none')}</MenuItem>
                    <MenuItem value="cash">{t('paymentMethods.cash')}</MenuItem>
                    <MenuItem value="bank_transfer">{t('paymentMethods.bank_transfer')}</MenuItem>
                    <MenuItem value="cheque">{t('paymentMethods.cheque')}</MenuItem>
                    <MenuItem value="card">{t('paymentMethods.card')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Property */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('property')}</InputLabel>
                  <Select
                    value={propertyId}
                    label={t('property')}
                    onChange={(e) => setPropertyId(e.target.value)}
                  >
                    <MenuItem value="">{t('common:none')}</MenuItem>
                    {properties?.map((property) => (
                      <MenuItem key={property.id} value={property.id}>
                        {property.title || property.property_code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Tenant */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tenant')}</InputLabel>
                  <Select
                    value={tenantId}
                    label={t('tenant')}
                    onChange={(e) => setTenantId(e.target.value)}
                  >
                    <MenuItem value="">{t('common:none')}</MenuItem>
                    {tenants?.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.first_name} {tenant.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('description')}
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('enterVoucherDescription')}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/dashboard/finance/vouchers/${id}`)}
                    disabled={saving}
                  >
                    {t('common:cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving || !amount}
                  >
                    {saving ? t('common:saving') : t('saveVoucher')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
}
